import React, { FC, useEffect } from 'react';
import { connect } from 'react-redux';

import { useLocation, useNavigate } from 'react-router-dom';

import Compose from '../../components/Compose';
import useAttachmentUpload from '../../hooks/attachments/useAttachmentUpload';
import useParamTemplateId from '../../hooks/router/params/useParamTemplateId';

import useParamTopicId from '../../hooks/router/params/useParamTopicId';
import useComposeType from '../../hooks/router/search/useComposeType';
import {
  getComposeTypeUrl,
  getOpenComposeUrl,
} from '../../routes/urls/searchParams/compose';
import { reportApiErrors } from '../../utils/error';

import { getIsInitialDraftChanged } from './helpers';
import useComposeActionsRedirect from './hooks/useComposeActionsRedirect';
import useComposeData from './hooks/useComposeData';
import useComposeSuccessToast from './hooks/useComposeSuccessToast';
import useSubmitData from './hooks/useSubmitData';
import mapDispatchToProps from './mapDispatchToProps';
import mapStateToProps from './mapStateToProps';
import { Props } from './props';

/**
 * Container for the compose
 *
 * @param props                       Props passed to the component
 * @param props.dataDraft             Draft data
 * @param props.isDefaultDraftChanged Whether the default draft has been changed
 * @param props.isDraftChanged        Whether the draft has been changed
 * @param props.nodeType              Whether the compose form is being used for messages or templates
 * @param props.requestDraftClear     Request that the draft is cleared
 * @returns                           The component itself
 */
const ComposeContainer: FC<Props> = ({
  dataDraft,
  isDefaultDraftChanged,
  isDraftChanged,
  nodeType,
  requestDraftClear,
}) => {
  const composeType = useComposeType();
  const location = useLocation();
  const navigate = useNavigate();
  const nodeId = useParamTemplateId();
  const topicId = useParamTopicId();

  const isInitialDraftChanged = getIsInitialDraftChanged(
    isDefaultDraftChanged,
    topicId,
    dataDraft?.templateTopicSaved,
  );

  const { requestCancelRedirect, requestSubmitRedirect } =
    useComposeActionsRedirect(nodeId);

  const { attachmentsFormatted, handleClearDraft } =
    useAttachmentUpload(nodeType);
  const existingChecklistIds = useComposeData(nodeId, nodeType, isDraftChanged);

  const { submitData, fetching = false } =
    useSubmitData(
      attachmentsFormatted,
      dataDraft,
      existingChecklistIds,
      nodeId,
      nodeType,
    ) ?? {};

  const openComposeUrl = getOpenComposeUrl(location, composeType);
  const composeTypeUrl = getComposeTypeUrl(
    location,
    dataDraft?.status === null ? 'post' : 'task',
  );

  useEffect(() => {
    // Add compose type to URL if it is not set and there is a draft saved
    if (composeType === null && isDraftChanged) {
      navigate(composeTypeUrl, { replace: true });
    }
  }, [composeType, composeTypeUrl, isDraftChanged, navigate]);

  useEffect(() => {
    if (composeType !== null && !isDraftChanged) {
      navigate(openComposeUrl, { replace: true });
    }
    // Open compose form only on first render after reload
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pushSuccessToast = useComposeSuccessToast();

  /**
   * Handle clearing the draft store
   *
   * @param shouldReset Whether the draft should be reset to initial state
   */
  const handleClear = (shouldReset = false) => {
    handleClearDraft();
    requestDraftClear(
      nodeType === 'MESSAGE' && topicId !== null && !shouldReset
        ? topicId
        : undefined,
    );
  };

  /**
   * Request clearing the compose form
   */
  const requestClear = () => {
    requestCancelRedirect();

    handleClear();
  };

  /**
   * Request closing the compose form
   */
  const requestClose = () => {
    requestSubmitRedirect();

    handleClear(true);
  };

  /**
   * The user has submitted the form, so we make a request to the server
   * to create/update message/template
   */
  const requestSubmit = () => {
    // We do not want to submit if there is a pending request
    if (fetching) {
      return;
    }

    submitData?.()
      .then(res => {
        pushSuccessToast(res);
        requestSubmitRedirect();
        handleClear(true);
      })
      .catch(reportApiErrors);
  };

  if (dataDraft === undefined) {
    return null;
  }

  return (
    <Compose
      isDraftChanged={isDraftChanged}
      isInitialDraftChanged={isInitialDraftChanged}
      isPending={fetching}
      nodeId={nodeId}
      nodeType={nodeType}
      requestClear={requestClear}
      requestClose={requestClose}
      requestSubmit={requestSubmit}
    />
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(ComposeContainer);
